package com.evoapps.evoshift.ui.courses

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.HtmlElementView
import kotlinx.browser.document
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

/** The web app shows presentations in the page. */
actual val presentationsShowInline: Boolean = true

private fun frame(title: String): HTMLIFrameElement = (document.createElement("iframe") as HTMLIFrameElement).apply {
    this.title = title
    setAttribute("style", "border:0;width:100%;height:100%;background:#ffffff;")
    setAttribute("allowfullscreen", "true")
    setAttribute("loading", "lazy")
    setAttribute("referrerpolicy", "strict-origin-when-cross-origin")
}

/**
 * A provider's player in a frame. Sandboxed: it runs its own scripts and may open its links in
 * a new tab, but never navigates or scripts the app's page. Only the hosts of firebase.json's
 * frame-src load (PresentationLinks.EMBED_HOSTS).
 */
@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun PresentationFrame(url: String, title: String, modifier: Modifier) {
    HtmlElementView(
        factory = {
            frame(title).apply {
                setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation")
                setAttribute("allow", "fullscreen")
                src = url
            }
        },
        modifier = modifier,
        update = { if (it.src != url) it.src = url }
    )
}

/**
 * An uploaded PDF in the browser's own viewer, from the bytes the app downloaded with the
 * person's sign-in (a blob: address of this page, revoked when the frame goes). Not sandboxed:
 * the browsers' PDF viewers refuse to run in a sandboxed frame, and the content is the
 * workplace's own file shown by the browser, not a page.
 */
@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun PdfFrame(bytes: ByteArray, title: String, modifier: Modifier) {
    val url = remember(bytes) {
        val data = Uint8Array(bytes.unsafeCast<Int8Array>().buffer)
        URL.createObjectURL(Blob(arrayOf(data), BlobPropertyBag(type = "application/pdf")))
    }
    DisposableEffect(url) { onDispose { URL.revokeObjectURL(url) } }
    HtmlElementView(
        factory = { frame(title).apply { src = url } },
        modifier = modifier,
        update = { if (it.src != url) it.src = url }
    )
}
